import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  X, 
  Check,
  PaperPlaneRight,
  Crop, 
  PencilSimple, 
  TextT, 
  Sticker,
  ArrowCounterClockwise
} from '@phosphor-icons/react';

const ImageEditor = ({ 
  imageFile, 
  onSave, 
  onCancel
}) => {
  const [image, setImage] = useState(null);
  const [mode, setMode] = useState('preview'); // preview, crop, draw, text, sticker
  const [caption, setCaption] = useState('');
  const [drawings, setDrawings] = useState([]);
  const [texts, setTexts] = useState([]);
  const [placedStickers, setPlacedStickers] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawColor, setDrawColor] = useState('#25D366');
  const [drawSize, setDrawSize] = useState(4);
  const [currentPath, setCurrentPath] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [showTextInput, setShowTextInput] = useState(false);
  
  // Crop state
  const [cropArea, setCropArea] = useState({ x: 10, y: 10, width: 80, height: 80 });
  const [isCropping, setIsCropping] = useState(false);
  const [cropHandle, setCropHandle] = useState(null);
  const [cropStart, setCropStart] = useState({ x: 0, y: 0 });
  
  // Drag state for texts and stickers
  const [dragItem, setDragItem] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Pinch/resize state for stickers
  const [resizingSticker, setResizingSticker] = useState(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // WhatsApp style colors
  const colors = [
    '#ffffff', '#000000', '#ff3b30', '#ff9500', '#ffcc00', 
    '#34c759', '#00c7be', '#007aff', '#5856d6', '#af52de', '#ff2d55'
  ];

  // Emoji stickers
  const emojiStickers = [
    '😀', '😂', '🥰', '😎', '🤩', '😭', '😱', '🤔',
    '👍', '👎', '❤️', '🔥', '💯', '🎉', '⭐', '💪',
    '🙏', '👏', '🤝', '✌️', '👋', '✨', '💖', '🌟'
  ];

  // Load image
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setImage(img);
          imageRef.current = img;
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  // Get canvas dimensions to fit screen
  const getCanvasDimensions = useCallback(() => {
    if (!image || !containerRef.current) return { width: 300, height: 400 };
    
    const maxWidth = containerRef.current.clientWidth - 16;
    const maxHeight = window.innerHeight - 180;
    
    let width = image.width;
    let height = image.height;
    
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = width * ratio;
    height = height * ratio;

    return { width: Math.round(width), height: Math.round(height) };
  }, [image]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !image) return;

    const { width, height } = getCanvasDimensions();
    canvas.width = width;
    canvas.height = height;

    // Draw image
    ctx.drawImage(image, 0, 0, width, height);

    // Draw all saved paths
    drawings.forEach(path => {
      if (path.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = path.color;
      ctx.lineWidth = path.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.moveTo(path.points[0].x, path.points[0].y);
      for (let i = 1; i < path.points.length; i++) {
        ctx.lineTo(path.points[i].x, path.points[i].y);
      }
      ctx.stroke();
    });

    // Draw current path
    if (currentPath.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = drawSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.moveTo(currentPath[0].x, currentPath[0].y);
      for (let i = 1; i < currentPath.length; i++) {
        ctx.lineTo(currentPath[i].x, currentPath[i].y);
      }
      ctx.stroke();
    }

    // Draw crop overlay
    if (mode === 'crop') {
      const cropX = (cropArea.x / 100) * width;
      const cropY = (cropArea.y / 100) * height;
      const cropW = (cropArea.width / 100) * width;
      const cropH = (cropArea.height / 100) * height;

      // Darken outside area
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, width, cropY);
      ctx.fillRect(0, cropY, cropX, cropH);
      ctx.fillRect(cropX + cropW, cropY, width - cropX - cropW, cropH);
      ctx.fillRect(0, cropY + cropH, width, height - cropY - cropH);
      
      // Draw crop border
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.strokeRect(cropX, cropY, cropW, cropH);
      
      // Draw grid lines (rule of thirds)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(cropX + (cropW / 3) * i, cropY);
        ctx.lineTo(cropX + (cropW / 3) * i, cropY + cropH);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cropX, cropY + (cropH / 3) * i);
        ctx.lineTo(cropX + cropW, cropY + (cropH / 3) * i);
        ctx.stroke();
      }

      // Draw corner + mid-side handles (8 total - WhatsApp style)
      const handleSize = 24;
      const handleThickness = 4;
      ctx.fillStyle = '#fff';
      
      // Top-left corner
      ctx.fillRect(cropX - 2, cropY - 2, handleSize, handleThickness);
      ctx.fillRect(cropX - 2, cropY - 2, handleThickness, handleSize);
      // Top-right corner
      ctx.fillRect(cropX + cropW - handleSize + 2, cropY - 2, handleSize, handleThickness);
      ctx.fillRect(cropX + cropW - 2, cropY - 2, handleThickness, handleSize);
      // Bottom-left corner
      ctx.fillRect(cropX - 2, cropY + cropH - 2, handleSize, handleThickness);
      ctx.fillRect(cropX - 2, cropY + cropH - handleSize + 2, handleThickness, handleSize);
      // Bottom-right corner
      ctx.fillRect(cropX + cropW - handleSize + 2, cropY + cropH - 2, handleSize, handleThickness);
      ctx.fillRect(cropX + cropW - 2, cropY + cropH - handleSize + 2, handleThickness, handleSize);
      
      // Top-middle
      ctx.fillRect(cropX + cropW / 2 - handleSize / 2, cropY - 2, handleSize, handleThickness);
      // Bottom-middle
      ctx.fillRect(cropX + cropW / 2 - handleSize / 2, cropY + cropH - 2, handleSize, handleThickness);
      // Middle-left
      ctx.fillRect(cropX - 2, cropY + cropH / 2 - handleSize / 2, handleThickness, handleSize);
      // Middle-right
      ctx.fillRect(cropX + cropW - 2, cropY + cropH / 2 - handleSize / 2, handleThickness, handleSize);
    }
  }, [image, drawings, currentPath, mode, cropArea, drawColor, drawSize, getCanvasDimensions]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Get coordinates from touch/mouse event
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };

  // Drawing handlers
  const handleDrawStart = (e) => {
    if (mode !== 'draw') return;
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoords(e);
    setCurrentPath([coords]);
  };

  const handleDrawMove = (e) => {
    if (!isDrawing || mode !== 'draw') return;
    e.preventDefault();
    const coords = getCoords(e);
    setCurrentPath(prev => [...prev, coords]);
  };

  const handleDrawEnd = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentPath.length > 1) {
      setDrawings(prev => [...prev, { points: currentPath, color: drawColor, size: drawSize }]);
    }
    setCurrentPath([]);
  };

  // Crop handlers
  const getCropHandle = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const { width, height } = getCanvasDimensions();
    const cropX = (cropArea.x / 100) * width;
    const cropY = (cropArea.y / 100) * height;
    const cropW = (cropArea.width / 100) * width;
    const cropH = (cropArea.height / 100) * height;
    
    const tolerance = 40;
    
    if (Math.abs(x - cropX) < tolerance && Math.abs(y - cropY) < tolerance) return 'tl';
    if (Math.abs(x - (cropX + cropW)) < tolerance && Math.abs(y - cropY) < tolerance) return 'tr';
    if (Math.abs(x - cropX) < tolerance && Math.abs(y - (cropY + cropH)) < tolerance) return 'bl';
    if (Math.abs(x - (cropX + cropW)) < tolerance && Math.abs(y - (cropY + cropH)) < tolerance) return 'br';
    // Mid-side handles
    if (Math.abs(x - (cropX + cropW / 2)) < tolerance && Math.abs(y - cropY) < tolerance) return 'tm';
    if (Math.abs(x - (cropX + cropW / 2)) < tolerance && Math.abs(y - (cropY + cropH)) < tolerance) return 'bm';
    if (Math.abs(x - cropX) < tolerance && Math.abs(y - (cropY + cropH / 2)) < tolerance) return 'ml';
    if (Math.abs(x - (cropX + cropW)) < tolerance && Math.abs(y - (cropY + cropH / 2)) < tolerance) return 'mr';
    if (x > cropX && x < cropX + cropW && y > cropY && y < cropY + cropH) return 'move';
    
    return null;
  };

  const handleCropStart = (e) => {
    if (mode !== 'crop') return;
    e.preventDefault();
    const coords = getCoords(e);
    const handle = getCropHandle(coords.x, coords.y);
    if (handle) {
      setIsCropping(true);
      setCropHandle(handle);
      setCropStart(coords);
    }
  };

  const handleCropMove = (e) => {
    if (!isCropping || mode !== 'crop' || !cropHandle) return;
    e.preventDefault();
    const coords = getCoords(e);
    const { width, height } = getCanvasDimensions();
    
    const deltaX = ((coords.x - cropStart.x) / width) * 100;
    const deltaY = ((coords.y - cropStart.y) / height) * 100;
    
    setCropArea(prev => {
      let newArea = { ...prev };
      
      if (cropHandle === 'move') {
        newArea.x = Math.max(0, Math.min(100 - prev.width, prev.x + deltaX));
        newArea.y = Math.max(0, Math.min(100 - prev.height, prev.y + deltaY));
      } else if (cropHandle === 'tl') {
        newArea.x = Math.max(0, Math.min(prev.x + prev.width - 10, prev.x + deltaX));
        newArea.y = Math.max(0, Math.min(prev.y + prev.height - 10, prev.y + deltaY));
        newArea.width = prev.width - (newArea.x - prev.x);
        newArea.height = prev.height - (newArea.y - prev.y);
      } else if (cropHandle === 'tr') {
        newArea.y = Math.max(0, Math.min(prev.y + prev.height - 10, prev.y + deltaY));
        newArea.width = Math.max(10, Math.min(100 - prev.x, prev.width + deltaX));
        newArea.height = prev.height - (newArea.y - prev.y);
      } else if (cropHandle === 'bl') {
        newArea.x = Math.max(0, Math.min(prev.x + prev.width - 10, prev.x + deltaX));
        newArea.width = prev.width - (newArea.x - prev.x);
        newArea.height = Math.max(10, Math.min(100 - prev.y, prev.height + deltaY));
      } else if (cropHandle === 'br') {
        newArea.width = Math.max(10, Math.min(100 - prev.x, prev.width + deltaX));
        newArea.height = Math.max(10, Math.min(100 - prev.y, prev.height + deltaY));
      } else if (cropHandle === 'tm') {
        newArea.y = Math.max(0, Math.min(prev.y + prev.height - 10, prev.y + deltaY));
        newArea.height = prev.height - (newArea.y - prev.y);
      } else if (cropHandle === 'bm') {
        newArea.height = Math.max(10, Math.min(100 - prev.y, prev.height + deltaY));
      } else if (cropHandle === 'ml') {
        newArea.x = Math.max(0, Math.min(prev.x + prev.width - 10, prev.x + deltaX));
        newArea.width = prev.width - (newArea.x - prev.x);
      } else if (cropHandle === 'mr') {
        newArea.width = Math.max(10, Math.min(100 - prev.x, prev.width + deltaX));
      }
      
      return newArea;
    });
    
    setCropStart(coords);
  };

  const handleCropEnd = () => {
    setIsCropping(false);
    setCropHandle(null);
  };

  // Apply crop
  const applyCrop = () => {
    const cropX = (cropArea.x / 100) * image.width;
    const cropY = (cropArea.y / 100) * image.height;
    const cropW = (cropArea.width / 100) * image.width;
    const cropH = (cropArea.height / 100) * image.height;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = cropW;
    tempCanvas.height = cropH;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    const newImg = new Image();
    newImg.onload = () => {
      setImage(newImg);
      imageRef.current = newImg;
      setCropArea({ x: 10, y: 10, width: 80, height: 80 });
      setMode('preview');
    };
    newImg.src = tempCanvas.toDataURL();
  };

  // Add text
  const addText = () => {
    if (!textInput.trim()) return;
    const { width, height } = getCanvasDimensions();
    setTexts(prev => [...prev, { 
      id: Date.now(),
      text: textInput, 
      x: width / 2, 
      y: height / 2, 
      color: textColor
    }]);
    setTextInput('');
    setShowTextInput(false);
  };

  // Add sticker
  const addSticker = (emoji) => {
    const { width, height } = getCanvasDimensions();
    setPlacedStickers(prev => [...prev, {
      id: Date.now(),
      emoji,
      x: width / 2,
      y: height / 2,
      size: 50
    }]);
    setMode('preview');
  };

  // Handle drag start for texts/stickers
  const handleItemDragStart = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches ? e.touches[0] : e;
    setDragItem({ item, type });
    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!dragItem) return;
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    const deltaX = touch.clientX - dragStart.x;
    const deltaY = touch.clientY - dragStart.y;

    if (dragItem.type === 'text') {
      setTexts(prev => prev.map(t => 
        t.id === dragItem.item.id ? { ...t, x: t.x + deltaX, y: t.y + deltaY } : t
      ));
    } else if (dragItem.type === 'sticker') {
      setPlacedStickers(prev => prev.map(s => 
        s.id === dragItem.item.id ? { ...s, x: s.x + deltaX, y: s.y + deltaY } : s
      ));
    }

    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleDragEnd = () => {
    setDragItem(null);
  };

  // Handle pinch resize for stickers
  const handleStickerResize = (stickerId, scaleFactor) => {
    setPlacedStickers(prev => prev.map(s => 
      s.id === stickerId ? { ...s, size: Math.max(20, Math.min(120, s.size * scaleFactor)) } : s
    ));
  };

  // Undo last action
  const handleUndo = () => {
    if (placedStickers.length > 0) {
      setPlacedStickers(prev => prev.slice(0, -1));
    } else if (texts.length > 0) {
      setTexts(prev => prev.slice(0, -1));
    } else if (drawings.length > 0) {
      setDrawings(prev => prev.slice(0, -1));
    }
  };

  // Save final image
  const handleSave = () => {
    const { width, height } = getCanvasDimensions();
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    
    const scaleX = image.width / width;
    const scaleY = image.height / height;

    // Draw image
    ctx.drawImage(image, 0, 0);

    // Draw all paths (scaled)
    drawings.forEach(path => {
      if (path.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = path.color;
      ctx.lineWidth = path.size * scaleX;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(path.points[0].x * scaleX, path.points[0].y * scaleY);
      for (let i = 1; i < path.points.length; i++) {
        ctx.lineTo(path.points[i].x * scaleX, path.points[i].y * scaleY);
      }
      ctx.stroke();
    });

    // Draw texts (scaled)
    texts.forEach(text => {
      ctx.font = `bold ${32 * scaleX}px Arial`;
      ctx.fillStyle = text.color;
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 4 * scaleX;
      ctx.shadowOffsetX = 2 * scaleX;
      ctx.shadowOffsetY = 2 * scaleY;
      ctx.textAlign = 'center';
      ctx.fillText(text.text, text.x * scaleX, text.y * scaleY);
      ctx.shadowColor = 'transparent';
    });

    // Draw stickers (scaled)
    placedStickers.forEach(sticker => {
      ctx.font = `${sticker.size * scaleX}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(sticker.emoji, sticker.x * scaleX, sticker.y * scaleY);
    });

    canvas.toBlob((blob) => {
      onSave(blob, caption.trim());
    }, 'image/jpeg', 0.9);
  };

  if (!image) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions();
  const hasEdits = drawings.length > 0 || texts.length > 0 || placedStickers.length > 0;

  return (
    <div 
      className="fixed inset-0 bg-black z-50 flex flex-col"
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      {/* Top Toolbar - WhatsApp Style */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#1f2c34]">
        <button onClick={onCancel} className="p-2 text-white hover:bg-white/10 rounded-full">
          <X size={26} weight="bold" />
        </button>
        
        <div className="flex items-center gap-1">
          {hasEdits && (
            <button onClick={handleUndo} className="p-2 text-white hover:bg-white/10 rounded-full" title="Undo">
              <ArrowCounterClockwise size={24} />
            </button>
          )}
          
          {/* Tool Icons */}
          <button
            onClick={() => setMode(mode === 'crop' ? 'preview' : 'crop')}
            className={`p-2.5 rounded-full transition-colors ${mode === 'crop' ? 'bg-[#25D366] text-white' : 'text-white hover:bg-white/10'}`}
          >
            <Crop size={24} />
          </button>
          <button
            onClick={() => setMode(mode === 'draw' ? 'preview' : 'draw')}
            className={`p-2.5 rounded-full transition-colors ${mode === 'draw' ? 'bg-[#25D366] text-white' : 'text-white hover:bg-white/10'}`}
          >
            <PencilSimple size={24} />
          </button>
          <button
            onClick={() => { setMode('text'); setShowTextInput(true); }}
            className={`p-2.5 rounded-full transition-colors ${mode === 'text' ? 'bg-[#25D366] text-white' : 'text-white hover:bg-white/10'}`}
          >
            <TextT size={24} />
          </button>
          <button
            onClick={() => setMode(mode === 'sticker' ? 'preview' : 'sticker')}
            className={`p-2.5 rounded-full transition-colors ${mode === 'sticker' ? 'bg-[#25D366] text-white' : 'text-white hover:bg-white/10'}`}
          >
            <Sticker size={24} />
          </button>
        </div>
        
        {/* Save button moved to bottom */}
        <div className="w-10" />
      </div>

      {/* Canvas Container */}
      <div 
        ref={containerRef}
        className="flex-1 flex items-center justify-center p-2 overflow-hidden relative"
      >
        <div className="relative" style={{ width: canvasWidth, height: canvasHeight }}>
          <canvas
            ref={canvasRef}
            onMouseDown={(e) => { handleDrawStart(e); handleCropStart(e); }}
            onMouseMove={(e) => { handleDrawMove(e); handleCropMove(e); }}
            onMouseUp={() => { handleDrawEnd(); handleCropEnd(); }}
            onMouseLeave={() => { handleDrawEnd(); handleCropEnd(); }}
            onTouchStart={(e) => { handleDrawStart(e); handleCropStart(e); }}
            onTouchMove={(e) => { handleDrawMove(e); handleCropMove(e); }}
            onTouchEnd={() => { handleDrawEnd(); handleCropEnd(); }}
            className="rounded-lg"
            style={{ touchAction: 'none' }}
          />

          {/* Text overlays - Draggable */}
          {texts.map(text => (
            <div
              key={text.id}
              className="absolute cursor-move select-none text-center font-bold text-2xl"
              style={{ 
                left: text.x - 50, 
                top: text.y - 15,
                color: text.color,
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                minWidth: '100px'
              }}
              onMouseDown={(e) => handleItemDragStart(e, text, 'text')}
              onTouchStart={(e) => handleItemDragStart(e, text, 'text')}
            >
              {text.text}
            </div>
          ))}

          {/* Sticker overlays - Draggable & Resizable */}
          {placedStickers.map(sticker => (
            <div
              key={sticker.id}
              className="absolute cursor-move select-none flex items-center justify-center"
              style={{ 
                left: sticker.x - sticker.size / 2, 
                top: sticker.y - sticker.size / 2,
                fontSize: sticker.size,
                width: sticker.size,
                height: sticker.size
              }}
              onMouseDown={(e) => handleItemDragStart(e, sticker, 'sticker')}
              onTouchStart={(e) => handleItemDragStart(e, sticker, 'sticker')}
            >
              {sticker.emoji}
              {/* Resize handles */}
              <button
                className="absolute -bottom-3 -right-3 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center text-xs"
                onMouseDown={(e) => { e.stopPropagation(); handleStickerResize(sticker.id, 1.2); }}
                onTouchStart={(e) => { e.stopPropagation(); handleStickerResize(sticker.id, 1.2); }}
              >
                +
              </button>
              <button
                className="absolute -bottom-3 -left-3 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center text-xs"
                onMouseDown={(e) => { e.stopPropagation(); handleStickerResize(sticker.id, 0.8); }}
                onTouchStart={(e) => { e.stopPropagation(); handleStickerResize(sticker.id, 0.8); }}
              >
                -
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Text Input Modal */}
      {showTextInput && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-20 p-4">
          <div className="bg-[#1f2c34] rounded-2xl p-5 w-full max-w-sm">
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type text..."
              className="w-full px-4 py-3 bg-[#2a3942] text-white rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-[#25D366] text-lg"
              autoFocus
            />
            
            {/* Color Picker */}
            <div className="flex gap-2 mb-4 justify-center flex-wrap">
              {colors.map(color => (
                <button
                  key={color}
                  onClick={() => setTextColor(color)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${textColor === color ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => { setShowTextInput(false); setTextInput(''); setMode('preview'); }}
                className="flex-1 py-3 bg-[#2a3942] text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={addText}
                className="flex-1 py-3 bg-[#25D366] text-white rounded-xl font-medium"
              >
                Add Text
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crop Apply Bar */}
      {mode === 'crop' && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
          <button 
            onClick={() => { setCropArea({ x: 10, y: 10, width: 80, height: 80 }); setMode('preview'); }} 
            className="px-6 py-3 bg-[#2a3942] text-white rounded-full font-medium"
          >
            Cancel
          </button>
          <button 
            onClick={applyCrop} 
            className="px-6 py-3 bg-[#25D366] text-white rounded-full font-medium"
          >
            Apply Crop
          </button>
        </div>
      )}

      {/* Drawing Color Picker + Done button */}
      {mode === 'draw' && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
          <div className="bg-[#1f2c34] rounded-full px-4 py-3 flex gap-2 items-center shadow-lg">
            {colors.slice(0, 8).map(color => (
              <button
                key={color}
                onClick={() => setDrawColor(color)}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${drawColor === color ? 'border-white scale-125' : 'border-gray-600'}`}
                style={{ backgroundColor: color }}
              />
            ))}
            <div className="border-l border-gray-600 pl-3 ml-2 flex items-center gap-2">
              <input
                type="range"
                min="2"
                max="12"
                value={drawSize}
                onChange={(e) => setDrawSize(Number(e.target.value))}
                className="w-16 accent-[#25D366]"
              />
            </div>
          </div>
          <button 
            onClick={() => setMode('preview')}
            className="px-8 py-2.5 bg-[#25D366] text-white rounded-full font-medium shadow-lg"
            data-testid="draw-done-btn"
          >
            Done
          </button>
        </div>
      )}

      {/* Sticker Picker */}
      {mode === 'sticker' && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#1f2c34] rounded-2xl p-4 shadow-lg w-[90%] max-w-xs">
          <p className="text-gray-400 text-xs mb-3 text-center">Tap to add • Drag to move • Use +/- to resize</p>
          <div className="grid grid-cols-8 gap-2">
            {emojiStickers.map((emoji, idx) => (
              <button
                key={idx}
                onClick={() => addSticker(emoji)}
                className="w-9 h-9 text-2xl hover:bg-[#2a3942] rounded-lg transition-colors flex items-center justify-center"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Caption Input + Send Button - WhatsApp Style (Bottom) */}
      {mode === 'preview' && (
        <div className="absolute bottom-0 left-0 right-0 bg-[#1f2c34] px-3 py-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-[#2a3942] rounded-full px-4 py-2">
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                className="w-full bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none"
                data-testid="image-caption-input"
              />
            </div>
            <button 
              onClick={handleSave} 
              className="w-12 h-12 bg-[#00a884] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg"
              data-testid="image-send-btn"
            >
              <PaperPlaneRight size={24} className="text-white" weight="fill" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageEditor;
